/**
 * The master checkboxes are identified by the 'data-master-checkbox' attribute,
    which holds an object with the container selector, label text, checked label text,
    and an optional selector to show/hide an element based on checkbox state.
    When a master checkbox is checked/unchecked, it controls the state of checkboxes
    within the specified container, updates the label text, and shows/hides the optional element.
 */

export default (() => {
  const masters = document.querySelectorAll('[data-master-checkbox]')
  if (masters.length === 0) return

  // Keep registry of all groups
  const groups = []

  // Build groups from master controls
  masters.forEach((master) => {
    const cfg = JSON.parse(master.getAttribute('data-master-checkbox') || '{}')
    const { container, label, labelChecked, showOnCheck } = cfg // eslint-disable-line no-unused-vars

    const containerEl = document.querySelector(container)
    if (!containerEl) {
      console.error(`[master-checkbox] Container not found: ${container}`)
      return
    }

    const masterInput = master.querySelector('input[type="checkbox"]')
    if (!masterInput) {
      console.error(
        '[master-checkbox] Master input[type="checkbox"] not found in:',
        master
      )
      return
    }

    const masterLabel =
      master.querySelector('label') ||
      document.querySelector(`label[for="${masterInput.id}"]`)

    const showOnCheckEl = showOnCheck
      ? document.querySelector(showOnCheck)
      : null

    const checkboxes = containerEl.querySelectorAll('input[type="checkbox"]')

    // UI hooks live inside the container (modal/form)
    const countEls = Array.from(
      containerEl.querySelectorAll('.master-checkbox-count')
    )
    const btnEls = Array.from(
      containerEl.querySelectorAll('.master-checkbox-btn')
    )

    const group = {
      masterEl: master,
      masterInput,
      masterLabel,
      showOnCheckEl,
      containerEl,
      checkboxes,
      countEls,
      btnEls,
      cfg,
    }
    groups.push(group)
  })

  // ----- helpers -----

  // Update label, count, buttons and master state based on children
  const refreshUI = (group) => {
    const {
      masterInput,
      masterLabel,
      showOnCheckEl,
      checkboxes,
      countEls,
      btnEls,
      cfg,
    } = group

    const selectedCount = Array.from(checkboxes).filter(
      (cb) => cb.checked
    ).length
    const anyChecked = selectedCount > 0

    // master reflects children
    masterInput.checked = anyChecked

    if (masterLabel && cfg.label && cfg.labelChecked) {
      masterLabel.textContent = anyChecked ? cfg.labelChecked : cfg.label
    }
    if (showOnCheckEl) {
      showOnCheckEl.classList.toggle('d-none', !anyChecked)
    }

    countEls.forEach((el) => {
      el.textContent = String(selectedCount)
    })
    btnEls.forEach((btn) => btn.classList.toggle('disabled', !anyChecked))
  }

  // Check/uncheck all children, then refresh
  const setAll = (group, checked) => {
    group.checkboxes.forEach((cb) => {
      cb.checked = checked
    })
    refreshUI(group)
  }

  // Unselect all other groups (pseudo-radio behavior)
  const clearOthers = (current) => {
    groups.forEach((g) => {
      if (g !== current) setAll(g, false)
    })
  }

  // ----- wire events -----

  groups.forEach((group) => {
    const { masterInput, checkboxes } = group

    // Master controls its container; when turned on, others are cleared
    masterInput.addEventListener('change', () => {
      const isOn = masterInput.checked
      if (isOn) clearOthers(group)
      setAll(group, isOn)
    })

    // Children sync back; selecting any also clears other groups
    checkboxes.forEach((cb) => {
      cb.addEventListener('change', () => {
        const anyChecked = Array.from(checkboxes).some((c) => c.checked)
        if (anyChecked) clearOthers(group)
        refreshUI(group)
      })
    })

    // Initial sync (supports pre-checked items)
    refreshUI(group)
  })

  // Custom reset: <button type="button" data-master-checkbox-reset="#selector">
  document.querySelectorAll('[data-master-checkbox-reset]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const targetSel = btn.getAttribute('data-master-checkbox-reset')
      if (!targetSel) return
      const target = document.querySelector(targetSel)
      if (!target) return

      // Uncheck all checkboxes within the target
      target.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.checked = false
      })

      // Refresh only affected groups (whose container is inside target or equals target)
      groups.forEach((group) => {
        if (
          target === group.containerEl ||
          target.contains(group.containerEl)
        ) {
          refreshUI(group)
        }
      })
    })
  })
})()
